import React from 'react'
import _ from 'lodash'
import { PoseGroup } from 'react-pose'
import { stripIndent } from 'common-tags'
import sizeMe from 'react-sizeme'
import * as styles from './styles'
import { UseCaseModal } from '../index'

function formatApiUrl(term) {
  return `/api/v1/${term}`
}

function isMoveCardsLeftPossible(position, itemsCount, windowSize) {
  return position + itemsCount > windowSize
}

function isMoveCardsRightPossible(position) {
  return position < 0
}

// https://stackoverflow.com/questions/37440408/how-to-detect-esc-key-press-in-react-and-how-to-handle-it
const ESC_KEY = 27

const items = [
  {
    id: 0,
    title: 'Recommend top 5 products which a user would most likely click',
    method: 'POST',
    urlPath: formatApiUrl('_recommend'),
    query: stripIndent`
    {
      "from": "impressions",
      "where": {
        "customerEmail": "kalle@aito.ai"
      },
      "recommend": "product",
      "goal": {
        "click": true
      },
      "limit": 5
    }
    `
  },
  {
    id: 1,
    title: 'Find colleagues who know about a given topic',
    method: 'POST',
    urlPath: formatApiUrl('_match'),
    query: stripIndent`
    {
      "from": "slack",
      "where": {
        "message": "React"
      },
      "match": "user"
    }
    `
  },
  {
    id: 2,
    title: 'Find out what attributes of the user correlates with making a purchase',
    method: 'POST',
    urlPath: formatApiUrl('_relate'),
    query: stripIndent`
    {
      "from": "impressions",
      "where": {
        "purchase": true
      },
      "relate": "user"
    }
    `
  },
  {
    id: 3,
    title: 'Search products for an ecommerce site based on a given search term',
    method: 'POST',
    urlPath: formatApiUrl('_search'),
    query: stripIndent`
    {
      "from" : "products",
      "where": {
        "title": { "$match": "iphone" }
      }
    }
    `
  },
  {
    id: 4,
    title: 'Find similar movies based on an example movie',
    method: 'POST',
    urlPath: formatApiUrl('_similarity'),
    query: stripIndent`
    {
      "from": "movies",
      "similarity": {
        "title": "Titanic",
        "releaseYear": 1997,
        "director": "James Cameron"
      }
    }
    `
  },
  {
    id: 5,
    title: 'Predict product categories from a given product description',
    method: 'POST',
    urlPath: formatApiUrl('_predict'),
    query: stripIndent`
    {
      "from": "products",
      "where": {
        "description": "
          A12 Bionic
          chip with next-generation
          Neural Engine
        "
      },
      "predict": "category"
    }
    `
  },
  {
    id: 6,
    title: 'Recommend a consultant for a given project and explain why',
    method: 'POST',
    urlPath: formatApiUrl('_recommend'),
    query: stripIndent`
    {
      "from": "project_allocations",
      "where": {
        "role": "Front-end developer",
        "project": {
          "location": "Helsinki",
          "industry": "Bank"
        },
        "person.available": true
      },
      "recommend": "person",
      "goal": {
        "project.success": true
      },
      "select": ["$why", "personId", "name"]
    }
    `
  },
  {
    id: 7,
    title: 'Find what correlates with a high revenue month',
    method: 'POST',
    urlPath: formatApiUrl('_relate'),
    query: stripIndent`
    {
      "from": "monthly_financials",
      "relate": {
        "revenue": { "$gt": 1000000 }
      }
    }
    `
  },
]

class UseCasesCarousel extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      scrollButtonWidth: 30,
      minCardWidth: 260,
      carouselPosition: 0,
      cardOpen: null,
      useCaseModalContent: null
    }
  }

  componentDidMount() {
    document.addEventListener('keydown', this.onKeyUp, false)
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.onKeyUp, false)
  }

  getPosition = () => {
    const { state } = this
    return state.carouselPosition
  }

  setPosition = (pos) => {
    this.setState({
      carouselPosition: pos,
    })
  }

  getCarouselWidth = () => {
    const { props, state } = this
    return props.size.width - 2 * state.scrollButtonWidth
  }

  getCarouselSize = () => {
    const { minCardWidth } = this.state
    const carouselWidth = this.getCarouselWidth()
    const floorCarouselSize = Math.floor(carouselWidth / minCardWidth)
    const carouselSize = floorCarouselSize !== 0 ? floorCarouselSize : 1
    const cardWidth = carouselWidth / carouselSize
    return { cardWidth, carouselSize }
  }

  isMoveCardsRightPossible = () => {
    return isMoveCardsRightPossible(this.getPosition())
  }

  isMoveCardsLeftPossible = () => {
    const { carouselSize } = this.getCarouselSize()
    return isMoveCardsLeftPossible(this.getPosition(), items.length, carouselSize)
  }

  moveCardsLeft = (_add) => {
    if (!this.isMoveCardsLeftPossible(_add)) {
      return
    }

    this.setPosition(this.getPosition() - 1)
  }

  moveCardsRight = () => {
    if (!this.isMoveCardsRightPossible()) {
      return
    }

    this.setPosition(this.getPosition() + 1)
  }

  moveModal = (currentId, direction) => {
    let item
    const indexOfCurrentItem = items.findIndex(i => i.id === currentId)
    if (direction === 'left') {
      if (currentId === items[items.length - 1].id) return
      item = items[indexOfCurrentItem + 1]
    } else {
      if (currentId === items[0].id) return
      item = items[indexOfCurrentItem - 1]
    }
    this.setState({
      useCaseModalContent: item,
      movingDirection: direction
    })
  }

  onKeyUp = (event) => {
    if (event.keyCode === ESC_KEY) {
      this.closeCard()
    }
  }

  onCardClick = (item, index) => {
    const { state } = this
    if (state.cardOpen !== null) {
      return
    }

    this.setState({
      cardOpen: index,
      useCaseModalContent: item,
      cardHover: null,
    })
  }

  onCardMouseEnter = (index) => {
    this.setState({
      cardHover: index,
    })
  }

  onCardMouseLeave = (index) => {
    const { state } = this
    if (state.cardHover === index) {
      this.setState({
        cardHover: null,
      })
    }
  }

  onClickOut = () => {
    this.closeCard()
  }

  closeCard = () => {
    this.setState({
      useCaseModalContent: null,
      cardOpen: null,
      cardHover: null,
    })
  }

  render() {
    const {
      scrollButtonWidth, cardOpen, cardHover, useCaseModalContent, movingDirection
    } = this.state
    const cardsContainerWidth = this.getCarouselWidth()
    const { cardWidth } = this.getCarouselSize()
    const moveNRight = this.getPosition()
    const carouselTransform = `translate(${cardWidth * moveNRight}px)`
    const isAnyCardOpen = cardOpen !== null
    return (
      <styles.Container>
        <styles.BackgroundFrame onClick={this.onClickOut} pose={isAnyCardOpen ? 'enter' : 'exit'} />
        <PoseGroup>
          {isAnyCardOpen
            && <UseCaseModal
              key="modal"
              items={items}
              item={useCaseModalContent}
              closeCard={this.closeCard}
              moveModal={this.moveModal}
              movingDirection={movingDirection}
            />
          }
        </PoseGroup>
        <styles.ScrollButton
          width={scrollButtonWidth}
          onClick={this.moveCardsRight}
          movePossible={this.isMoveCardsRightPossible()}
        >
          <styles.ChevronLeftIcon />
        </styles.ScrollButton>

        <styles.UseCaseCardsContainer width={cardsContainerWidth}>
          <styles.UseCaseCards transform={carouselTransform}>
            {_.map(items, (item, itemIndex) => {
              const isCardOpen = cardOpen === itemIndex
              const isCardHovered = cardHover === itemIndex

              const onHoverCallbackProps = isCardOpen ? {} : {
                onMouseEnter: () => this.onCardMouseEnter(itemIndex),
                onMouseLeave: () => this.onCardMouseLeave(itemIndex),
              }
              return (
                <styles.CardListItem itemIndex={itemIndex} open={isCardOpen} key={itemIndex}>
                  <styles.CardContainer width={cardWidth}>
                    <styles.UseCaseCard
                      open={isCardOpen}
                      hover={isCardHovered}
                      onClick={() => this.onCardClick(item, itemIndex)}
                      {...onHoverCallbackProps}
                    >
                      <PoseGroup>
                        {
                          (isCardHovered && !isCardOpen)
                            ? <styles.UseCaseCardOverlay key="overlay"><span>See query</span></styles.UseCaseCardOverlay>
                            : null
                        }
                      </PoseGroup>
                      <styles.UseCaseCardTitle pose={isCardHovered ? 'hover' : 'init'}>
                        {item.title}
                      </styles.UseCaseCardTitle>
                    </styles.UseCaseCard>
                  </styles.CardContainer>
                </styles.CardListItem>
              )
            })}
          </styles.UseCaseCards>
        </styles.UseCaseCardsContainer>

        <styles.ScrollButton
          width={scrollButtonWidth}
          onClick={this.moveCardsLeft}
          movePossible={this.isMoveCardsLeftPossible()}
        >
          <styles.ChevronRightIcon />
        </styles.ScrollButton>

      </styles.Container>
    )
  }
}

export default sizeMe({ refreshRate: 40 })(UseCasesCarousel)
